import type React from 'react'
import type { CSSProperties } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { BottomNavigation, type BottomNavigationItem } from '@components/ui/BottomNavigation'
import { Header, type HeaderProps } from '@components/ui/Header'

export type MainNavKey = 'home' | 'store' | 'history'

export type SecondaryLayout = 'default' | 'qr'

interface LayoutHeaderConfig extends HeaderProps {
  enabled?: boolean
  containerClassName?: string
  subTitle?: string
  subTitleClassName?: string
  offset?: number
  scrolledClassName?: string
}

interface MainLayoutProps {
  children: React.ReactNode
  activeMain?: MainNavKey | null
  secondaryLayout?: SecondaryLayout
  header?: LayoutHeaderConfig | null
  showBottomNav?: boolean
  outerClassName?: string
  className?: string
  contentClassName?: string
  bottomNavClassName?: string
  bottomNavOffset?: number
  bottomNavScrolledClassName?: string
}

interface NavConfigItem extends BottomNavigationItem {
  path?: string
}

const MAIN_NAV_ITEMS: NavConfigItem[] = [
  { id: 'home', icon: 'home', label: 'ホーム', path: '/home' },
  { id: 'store', icon: 'store', label: '購入', path: '/store' },
  { id: 'send', icon: 'send', label: '送金', path: '/transfer' },
  { id: 'history', icon: 'history', label: '履歴', path: '/history' },
]

const QUICK_DEFAULT: NavConfigItem[] = [
  { id: 'scan', icon: 'scan', label: 'スキャン', path: '/qr' },
]

export const MainLayout: React.FC<MainLayoutProps> = ({
  children,
  activeMain = 'home',
  secondaryLayout = 'default',
  header = null,
  showBottomNav = true,
  outerClassName,
  className,
  contentClassName,
  bottomNavClassName,
  bottomNavOffset,
  bottomNavScrolledClassName,
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const locationState = (location.state as { returnTo?: string } | null) ?? null
  const returnTo = locationState?.returnTo ?? '/home'
  const mainRef = useRef<HTMLElement | null>(null)
  const [isScrolled, setIsScrolled] = useState(false)

  const handleMainSelect = (id: string) => {
    const target = MAIN_NAV_ITEMS.find((item) => item.id === id)
    if (target?.path && location.pathname !== target.path) {
      navigate(target.path)
    }
  }

  const quickPrimaryItems: NavConfigItem[] =
    secondaryLayout === 'qr'
      ? [
          { id: 'qr', icon: 'qr', label: '受け取る', path: '/qr' },
          { id: 'scan', icon: 'scan', label: 'スキャン', path: '/qr/scan' },
        ]
      : QUICK_DEFAULT

  const quickSecondaryItems: NavConfigItem[] | null =
    secondaryLayout === 'qr'
      ? [{ id: 'close', icon: 'close', label: '閉じる', path: returnTo }]
      : null

  const safeAreaTop = 'env(safe-area-inset-top, 0px)'
  const safeAreaBottom = 'env(safe-area-inset-bottom, 0px)'

  const quickActiveEntry = quickPrimaryItems.find((item) => item.path === location.pathname) ?? null
  const activeQuickId = quickActiveEntry?.id ?? null

  const handleQuickSelect = (items: NavConfigItem[]) => (id: string) => {
    const target = items.find((item) => item.id === id)
    if (target?.path && location.pathname !== target.path) {
      if (secondaryLayout === 'qr') {
        navigate(target.path, { state: { returnTo, from: location.pathname } })
        return
      }
      if (target.path.startsWith('/qr')) {
        navigate(target.path, { state: { returnTo: location.pathname } })
        return
      }
      navigate(target.path)
    }
  }

  const handleScroll = useCallback((event: React.UIEvent<HTMLElement>) => {
    setIsScrolled(event.currentTarget.scrollTop > 0)
  }, [])

  useEffect(() => {
    const node = mainRef.current
    if (!node) return
    setIsScrolled(node.scrollTop > 0)
  }, [])

  const outerClasses = [
    'min-h-screen w-full bg-[#F5F5F5] text-foreground flex items-start justify-center',
    outerClassName ?? '',
  ]
    .filter(Boolean)
    .join(' ')

  const innerClasses = [
    'relative flex w-full max-w-[480px] flex-col text-foreground overflow-hidden',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ')

  const outerStyle: CSSProperties = { maxHeight: '100dvh', height: '100dvh' }
  const innerStyle: CSSProperties = { maxHeight: '100dvh', height: '100dvh' }

  const headerConfig = header && header.enabled !== false ? header : null
  const headerOffset = headerConfig ? (headerConfig.offset ?? 80) : 0

  let headerNode: React.ReactNode = null
  if (headerConfig) {
    const {
      containerClassName,
      subTitle,
      subTitleClassName,
      enabled: _enabled,
      offset: _offset,
      scrolledClassName,
      ...headerProps
    } = headerConfig
    const headerContainerClasses = [
      'w-full transition-[background-color,backdrop-filter,box-shadow] duration-200 ease-out',
      containerClassName ?? 'bg-[#f6f6f6]',
    ]
      .filter(Boolean)
      .join(' ')
    const headerScrolledClasses =
      scrolledClassName ?? 'supports-[backdrop-filter]:backdrop-blur-[1px] '
    const subtitleClasses = ['px-4 pb-2 text-sm text-zinc-500', subTitleClassName ?? '']
      .filter(Boolean)
      .join(' ')

    headerNode = (
      <div
        className={`${headerContainerClasses} ${isScrolled ? headerScrolledClasses : ''}`}
        style={{ paddingTop: safeAreaTop }}
      >
        <Header {...headerProps} />
        {subTitle ? <div className={subtitleClasses}>{subTitle}</div> : null}
      </div>
    )
  }

  const bottomOffset = showBottomNav ? (bottomNavOffset ?? 88) : 0

  const mainClasses = ['flex-1 overflow-y-auto', contentClassName ?? ''].filter(Boolean).join(' ')

  const mainStyle: CSSProperties = {}
  if (headerOffset) {
    mainStyle.paddingTop = `calc(${headerOffset}px + ${safeAreaTop})`
  } else {
    mainStyle.paddingTop = safeAreaTop
  }
  if (bottomOffset) {
    mainStyle.paddingBottom = `calc(${bottomOffset}px + ${safeAreaBottom})`
  } else {
    mainStyle.paddingBottom = safeAreaBottom
  }

  const bottomNavClasses = [
    'bg-inherit px-6 pt-4 transition-[background-color,backdrop-filter,box-shadow] duration-200 ease-out',
    bottomNavClassName ?? '',
    isScrolled
      ? (bottomNavScrolledClassName ?? 'supports-[backdrop-filter]:backdrop-blur-[1px]')
      : '',
  ]
    .filter(Boolean)
    .join(' ')

  const bottomNavStyle: CSSProperties = {
    paddingBottom: `calc(${safeAreaBottom} + var(--bottom-nav-base-padding, 1.5rem))`,
  }

  const bottomNavNode = showBottomNav ? (
    <div className={bottomNavClasses} style={bottomNavStyle}>
      {secondaryLayout === 'qr' ? (
        <div className="flex items-center justify-between gap-6">
          <BottomNavigation
            items={quickPrimaryItems}
            activeId={activeQuickId}
            onSelect={handleQuickSelect(quickPrimaryItems)}
          />
          {quickSecondaryItems ? (
            <BottomNavigation
              items={quickSecondaryItems}
              activeId={null}
              onSelect={handleQuickSelect(quickSecondaryItems)}
            />
          ) : null}
        </div>
      ) : (
        <div className="flex items-center justify-between gap-6">
          <BottomNavigation
            items={MAIN_NAV_ITEMS}
            activeId={activeMain === null ? null : activeMain}
            onSelect={handleMainSelect}
          />
          {quickPrimaryItems.length > 0 ? (
            <BottomNavigation
              items={quickPrimaryItems}
              activeId={activeQuickId}
              onSelect={handleQuickSelect(quickPrimaryItems)}
            />
          ) : null}
        </div>
      )}
    </div>
  ) : null

  return (
    <div className={outerClasses} style={outerStyle}>
      <div className={innerClasses} style={innerStyle}>
        {headerNode ? (
          <div className="pointer-events-none absolute inset-x-0 top-0 z-30">
            <div className="pointer-events-auto">{headerNode}</div>
          </div>
        ) : null}
        <main ref={mainRef} className={mainClasses} style={mainStyle} onScroll={handleScroll}>
          {children}
        </main>
        {bottomNavNode ? (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 z-30">
            <div className="pointer-events-auto">{bottomNavNode}</div>
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default MainLayout
